import JSONNodeDisplay from "@/components/common/shared/JSONViewer/JSONNodeDisplay"
import useSnackbar from "@/helpers/add-snackbar"
import { makeStyles } from "@material-ui/core/styles"
import { Build, Tune, PlayArrow } from "@material-ui/icons"
import { jsonrepair } from "jsonrepair"
import React, { useState, useRef } from "react"
import PasteButton from "../buttons/utility/PasteButton"

interface JsonViewerWithCopyProps {
  jsonData?: object
}

export const useStyles = makeStyles(theme => ({
  container: {
    minHeight: "auto",
    transition: "all 0.3s ease",
    backgroundColor: "inherit"
  },
  contentWrapper: {
    margin: "0 auto",
    display: "block",
    backgroundColor: "inherit"
  },
  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    width: "100%",
    maxWidth: 400
  },
  searchInput: {
    flex: 1,
    padding: theme.spacing(1),
    border: "1px solid #ddd",
    borderRadius: 4,
    fontSize: "0.9rem",
    "&:focus": {
      outline: "none",
      borderColor: "#1976d2",
      boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)"
    },
    "&::placeholder": {
      color: "#666"
    }
  },
  minimizeButton: {
    position: "relative",
    marginLeft: "8px",
    padding: "4px 8px",
    fontSize: "0.8rem",
    background: "#f0f0f0",
    border: "1px solid #ddd",
    borderRadius: "4px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#e0e0e0"
    }
  },
  inputSection: {
    backgroundColor: "inherit",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#333",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center"
  },
  textarea: {
    width: "100%",
    border: "2px solid #d0d0d0",
    borderRadius: 8,
    padding: theme.spacing(2),
    fontFamily: "monospace",
    fontSize: "0.9rem",
    resize: "none",
    transition: "border-color 0.3s, box-shadow 0.3s",
    "&:focus": {
      outline: "none",
      borderColor: "#1976d2",
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)"
    }
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  button: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#fff",
    padding: `${theme.spacing(1.5)}px ${theme.spacing(3)}px`,
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    transition: "all 0.3s",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      transform: "translateY(-1px)"
    }
  },
  loadButton: {
    backgroundColor: "#1976d2",
    "&:hover": {
      backgroundColor: "#1565c0"
    }
  },
  fixButton: {
    backgroundColor: "#2e7d32",
    "&:hover": {
      backgroundColor: "#1b5e20"
    }
  },
  exampleButton: {
    backgroundColor: "#7b1fa2",
    "&:hover": {
      backgroundColor: "#6a1b9a"
    }
  },
  viewerSection: {
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    overflow: "hidden"
  },
  viewerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
    background: "linear-gradient(to right, #fafafa, #f0f0f0)",
    borderBottom: "1px solid #e0e0e0"
  },
  headerButtonGroup: {
    display: "flex",
    gap: theme.spacing(1)
  },
  headerButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "#fff",
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    fontSize: "0.875rem",
    "&:hover": {
      boxShadow: "0 2px 6px rgba(0,0,0,0.3)"
    }
  },
  copyButton: {
    backgroundColor: "#1976d2",
    "&:hover": {
      backgroundColor: "#1565c0"
    }
  },
  collapseButton: {
    backgroundColor: "#7b1fa2",
    "&:hover": {
      backgroundColor: "#6a1b9a"
    }
  },
  viewerContent: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    overflow: "visible",
    color: "#1e1e1e",
    fontFamily: "monospace",
    fontSize: "0.95rem"
  },
  jsonGroup: {
    display: "inline-flex",
    alignItems: "center"
  },
  copyButtons: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginLeft: theme.spacing(1),
    opacity: 1
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "#888",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: "0.875rem"
    }
  },
  valueButton: {
    "&:hover": {
      backgroundColor: "#1976d2",
      color: "#fff"
    }
  },
  pathButton: {
    "&:hover": {
      backgroundColor: "#2e7d32",
      color: "#fff"
    }
  },
  prettifyButton: {
    backgroundColor: "#6f42c1",
    "&:hover": {
      backgroundColor: "#6f42c1"
    }
  },
  formattedButton: {
    "&:hover": {
      backgroundColor: "#7b1fa2",
      color: "#fff"
    }
  },
  nullValue: {
    color: "#9e9e9e"
  },
  booleanValue: {
    color: "#ff9800"
  },
  numberValue: {
    color: "#ba68c8"
  },
  stringValue: {
    color: "#66bb6a"
  },
  keyName: {
    color: "#42a5f5"
  },
  punctuation: {
    color: "#9e9e9e"
  },
  collapsible: {
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    transition: "background-color 0.2s"
  },
  treeContainer: {
    display: "inline-block"
  },
  nestedContainer: {
    marginLeft: theme.spacing(3),
    borderLeft: "2px solid #424242",
    paddingLeft: theme.spacing(1.5)
  },
  arrayItem: {
    paddingTop: 4,
    paddingBottom: 4,
    display: "flex",
    alignItems: "flex-start"
  },
  shrink: {
    flexShrink: 0
  },
  inlineBlock: {
    marginLeft: 4,
    display: "inline-block"
  },
  altBackground: {
    backgroundColor: "rgba(255, 255, 220, 1)"
  },
  altKeyBackground: {
    backgroundColor: "rgba(255, 255, 220, 1)"
  },
  altIndexBackground: {
    backgroundColor: "rgba(248, 248, 248, 1)"
  }
}))

const JsonViewerWithCopy: React.FC<JsonViewerWithCopyProps> = ({
  jsonData
}) => {
  const [inputCollapsed, setInputCollapsed] = useState<boolean>(false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const classes = useStyles()
  const { addSnackbar } = useSnackbar()
  const [data, setData] = useState<object>(jsonData || {})
  const [input, setInput] = useState("")
  const isInputVisible = !jsonData

  const handlePasteComplete = (text: string) => {
    try {
      const parsed = JSON.parse(text)
      setInput(JSON.stringify(parsed, null, 2))
      setData(parsed)
      addSnackbar("JSON pasted and formatted successfully")
    } catch (err) {
      try {
        const fixed = jsonrepair(text)
        const parsed = JSON.parse(fixed)
        setInput(JSON.stringify(parsed, null, 2))
        setData(parsed)
        addSnackbar("JSON repaired and formatted successfully")
      } catch (error) {
        addSnackbar("Invalid JSON format! Cannot repair.", "error")
      }
    }
  }

  const handleLoadJson = () => {
    try {
      const parsed = JSON.parse(input)
      setData(parsed)
      addSnackbar("JSON loaded successfully")
    } catch (e) {
      addSnackbar("Invalid JSON format!", "error")
    }
  }

  const handleFixJson = () => {
    try {
      const fixed = jsonrepair(input)
      const parsed = JSON.parse(fixed)
      setInput(JSON.stringify(parsed, null, 2))
      setData(parsed)
      addSnackbar("JSON repaired successfully ✅")
    } catch (err) {
      addSnackbar("Unable to fix JSON. Please verify your input.", "error")
    }
  }

  const handlePrettifyJson = () => {
    try {
      const parsed = JSON.parse(input)
      setInput(JSON.stringify(parsed, null, 2))
      addSnackbar("JSON prettified successfully ✅")
    } catch (err) {
      addSnackbar("Invalid JSON format! Cannot prettify.", "error")
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.contentWrapper}>
        {isInputVisible && (
          <div className={classes.inputSection}>
            <h3 className={classes.sectionTitle}>
              JSON Input
              <button
                onClick={() => setInputCollapsed(!inputCollapsed)}
                className={classes.minimizeButton}
              >
                {inputCollapsed ? "▶" : "▼"}
              </button>
            </h3>
            {!inputCollapsed && (
              <>
                <textarea
                  ref={textAreaRef}
                  className={classes.textarea}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Paste your JSON here..."
                  rows={8}
                />
                <div className={classes.buttonGroup}>
                  <PasteButton
                    onPaste={handlePasteComplete}
                    textAreaRef={textAreaRef}
                    buttonText="Paste & Format JSON"
                    size="small"
                  />
                  <button
                    onClick={handleLoadJson}
                    className={`${classes.button} ${classes.loadButton}`}
                  >
                    <PlayArrow />
                    Load JSON
                  </button>
                  <button
                    onClick={handleFixJson}
                    className={`${classes.button} ${classes.fixButton}`}
                  >
                    <Build />
                    Fix JSON
                  </button>
                  <button
                    onClick={handlePrettifyJson}
                    className={`${classes.button} ${classes.prettifyButton}`}
                  >
                    <Tune />
                    Prettify
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <JSONNodeDisplay data={data} />
      </div>
    </div>
  )
}

export default JsonViewerWithCopy
